import React, { useState, useEffect } from 'react';
import './save.css';
import Navbar from '../Navbar/nbar';
import Footer from '../footer/Footer';

/* ═══════════════════════════════════════
   SOMFIN Landing Hero — First Page
   The gateway to financial clarity
   ═══════════════════════════════════════ */

const FEATURES = [
  { icon: 'health', title: 'Financial Health Score', desc: 'A personalised score based on your real habits — not just demographics. Know exactly where you stand.' },
  { icon: 'budget', title: 'Smart Budget Engine', desc: 'See your 50-30-20 split in real time. Adjust sliders, watch your savings rate change instantly.' },
  { icon: 'sip', title: 'Wealth Projections', desc: 'SIP calculator with step-up modeling. See how ₹5,000/month becomes ₹1.9 Cr over 25 years.' },
  { icon: 'learn', title: 'Learn & Assess', desc: 'Quizzes, glossary, occupation-specific guides — financial literacy built around your life.' },
  { icon: 'tax', title: 'Tax Optimiser', desc: 'Old vs New regime comparison. 80C, 80D, NPS, HRA — see exactly how much you save.' },
  { icon: 'track', title: 'Milestone Tracking', desc: 'Check off financial goals as you achieve them. Emergency fund, SIP, insurance — gamified progress.' },
];

const STATS = [
  { val: '50+', label: 'Financial Tools' },
  { val: '1,000+', label: 'Users Onboarded' },
  { val: '96%', label: 'Satisfaction Rate' },
  { val: 'Free', label: 'To Get Started' },
];

function FeatureIcon({ type }) {
  const paths = {
    health: <><path d="M12 21C12 21 4 14 4 8.5C4 5.46 6.46 3 9.5 3C11.06 3 12 4 12 4S12.94 3 14.5 3C17.54 3 20 5.46 20 8.5C20 14 12 21 12 21Z" fill="none" strokeWidth="1.8" strokeLinejoin="round"/></>,
    budget: <><rect x="3" y="3" width="18" height="18" rx="3" fill="none" strokeWidth="1.8"/><line x1="3" y1="9" x2="21" y2="9" strokeWidth="1.5"/><line x1="9" y1="21" x2="9" y2="9" strokeWidth="1.5"/></>,
    sip: <><polyline points="3 17 9 11 13 15 21 7" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="17 7 21 7 21 11" fill="none" strokeWidth="2" strokeLinecap="round"/></>,
    learn: <><path d="M2 7l10-4 10 4-10 4L2 7z" fill="none" strokeWidth="1.8" strokeLinejoin="round"/><path d="M6 9v6c0 2 2.7 4 6 4s6-2 6-4V9" fill="none" strokeWidth="1.8"/></>,
    tax: <><rect x="5" y="3" width="14" height="18" rx="2" fill="none" strokeWidth="1.8"/><line x1="9" y1="8" x2="15" y2="8" strokeWidth="1.5" strokeLinecap="round"/><line x1="9" y1="12" x2="15" y2="12" strokeWidth="1.5" strokeLinecap="round"/><line x1="9" y1="16" x2="12" y2="16" strokeWidth="1.5" strokeLinecap="round"/></>,
    track: <><circle cx="12" cy="12" r="9" fill="none" strokeWidth="1.8"/><polyline points="9 12 11 14 15 10" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></>,
  };
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeLinecap="round">
      {paths[type]}
    </svg>
  );
}

const Save = () => {
  const [tipIdx, setTipIdx] = useState(0);
  const tips = [
    '₹200/day on small expenses = ₹73,000/year wasted',
    '₹500/month SIP at 12% for 30 years = ₹17.6 lakh',
    'Step-up SIP by 10% yearly triples your final corpus',
    'Health insurance before 30 is 40–50% cheaper than at 45',
    'Direct mutual fund plans save 0.5–1% vs regular — lakhs over 20 years',
    'CIBIL 750+ saves you 1–2% on every loan',
  ];
  useEffect(() => {
    const t = setInterval(() => setTipIdx(p => (p + 1) % tips.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="sv__page">
      <Navbar />

      {/* ─── HERO ─── */}
      <div className="sv__hero">
        <svg className="sv__hero-art" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
          <circle cx="200" cy="150" r="120" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1">
            <animate attributeName="r" values="118;128;118" dur="6s" repeatCount="indefinite"/>
          </circle>
          <circle cx="1000" cy="200" r="160" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1">
            <animate attributeName="r" values="158;170;158" dur="8s" repeatCount="indefinite"/>
          </circle>
          <circle cx="600" cy="500" r="90" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="1">
            <animate attributeName="r" values="88;96;88" dur="5s" repeatCount="indefinite"/>
          </circle>
          <line x1="320" y1="150" x2="510" y2="500" stroke="rgba(79,41,126,0.03)" strokeWidth="0.5"/>
          <line x1="690" y1="500" x2="840" y2="200" stroke="rgba(26,138,99,0.03)" strokeWidth="0.5"/>
          <path d="M 100 450 Q 350 200 600 320 T 1100 180" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="1.5" strokeDasharray="8 5"/>
          <path d="M 50 350 Q 300 500 600 380 T 1150 280" fill="none" stroke="rgba(26,138,99,0.03)" strokeWidth="1" strokeDasharray="5 7"/>
        </svg>

        <div className="sv__hero-grid">
          <div className="sv__hero-content">
            <span className="sv__tag">India's Financial Literacy Platform</span>
            <div className="sv__hero-title">Master Your Money.<br/>Build Your Future.</div>
            <p className="sv__hero-sub">Free interactive tools that turn financial confusion into clarity — personalised health scores, smart budgets, SIP projections, tax optimisation, and 50+ calculators built for Indian finances.</p>
            <div className="sv__hero-actions">
              <a href="/finlit" className="sv__btn-primary">Open Financial Dashboard <span>→</span></a>
              <a href="/explore" className="sv__btn-outline">Explore Platform</a>
            </div>
            <div className="sv__hero-tip">
              <span className="sv__hero-tip-label">Did you know?</span>
              <span className="sv__hero-tip-text" key={tipIdx}>{tips[tipIdx]}</span>
            </div>
          </div>

          {/* Hero SVG Illustration — Financial Dashboard Preview */}
          <div className="sv__hero-illust">
            <svg viewBox="0 0 320 360" fill="none" className="sv__hero-svg">
              <rect x="10" y="10" width="300" height="340" rx="18" fill="#fff" stroke="#E8E8F0" strokeWidth="1.5"/>
              {/* Header */}
              <rect x="24" y="24" width="80" height="10" rx="3" fill="#4f297e" opacity="0.15"/>
              <rect x="24" y="40" width="50" height="6" rx="2" fill="#E8E8F0"/>
              <rect x="24" y="54" width="272" height="1" fill="#F0F0F5"/>
              {/* Health score ring */}
              <circle cx="70" cy="105" r="32" fill="none" stroke="#E8E8F0" strokeWidth="6"/>
              <circle cx="70" cy="105" r="32" fill="none" stroke="#1A8A63" strokeWidth="6" strokeDasharray="140 60" strokeLinecap="round" transform="rotate(-90 70 105)" opacity="0.6"/>
              <text x="70" y="102" textAnchor="middle" fill="#1A8A63" fontSize="12" fontWeight="700" opacity="0.6">72</text>
              <text x="70" y="114" textAnchor="middle" fill="#8E90A6" fontSize="6">SCORE</text>
              {/* Profile cards */}
              <rect x="120" y="70" width="84" height="34" rx="6" fill="#F9F6FC" stroke="#E8E8F0" strokeWidth="0.8"/>
              <rect x="128" y="78" width="32" height="4" rx="1" fill="#4f297e" opacity="0.2"/>
              <rect x="128" y="86" width="50" height="8" rx="2" fill="#4f297e" opacity="0.08"/>
              <rect x="212" y="70" width="84" height="34" rx="6" fill="#E8F5EE" stroke="#E8E8F0" strokeWidth="0.8"/>
              <rect x="220" y="78" width="32" height="4" rx="1" fill="#1A8A63" opacity="0.2"/>
              <rect x="220" y="86" width="50" height="8" rx="2" fill="#1A8A63" opacity="0.08"/>
              <rect x="120" y="110" width="84" height="34" rx="6" fill="#FDF6E3" stroke="#E8E8F0" strokeWidth="0.8"/>
              <rect x="128" y="118" width="32" height="4" rx="1" fill="#A67C00" opacity="0.2"/>
              <rect x="128" y="126" width="50" height="8" rx="2" fill="#A67C00" opacity="0.08"/>
              <rect x="212" y="110" width="84" height="34" rx="6" fill="#F9F6FC" stroke="#E8E8F0" strokeWidth="0.8"/>
              <rect x="220" y="118" width="32" height="4" rx="1" fill="#4f297e" opacity="0.2"/>
              <rect x="220" y="126" width="50" height="8" rx="2" fill="#4f297e" opacity="0.08"/>
              {/* Divider */}
              <rect x="24" y="156" width="272" height="1" fill="#F0F0F5"/>
              {/* Bar chart */}
              <rect x="34" y="260" width="26" height="55" rx="4" fill="#4f297e" opacity="0.12"/>
              <rect x="68" y="235" width="26" height="80" rx="4" fill="#4f297e" opacity="0.18"/>
              <rect x="102" y="210" width="26" height="105" rx="4" fill="#4f297e" opacity="0.25"/>
              <rect x="136" y="220" width="26" height="95" rx="4" fill="#4f297e" opacity="0.22"/>
              <rect x="170" y="195" width="26" height="120" rx="4" fill="#1A8A63" opacity="0.3"/>
              <rect x="204" y="180" width="26" height="135" rx="4" fill="#1A8A63" opacity="0.4"/>
              <rect x="238" y="170" width="26" height="145" rx="4" fill="#1A8A63" opacity="0.5"/>
              {/* Donut chart */}
              <circle cx="280" cy="180" r="22" fill="none" stroke="#E8E8F0" strokeWidth="7"/>
              <circle cx="280" cy="180" r="22" fill="none" stroke="#4f297e" strokeWidth="7" strokeDasharray="70 70" strokeLinecap="round" transform="rotate(-90 280 180)" opacity="0.45"/>
              <circle cx="280" cy="180" r="22" fill="none" stroke="#1A8A63" strokeWidth="7" strokeDasharray="30 108" strokeLinecap="round" transform="rotate(110 280 180)" opacity="0.45"/>
              {/* Bottom labels */}
              <rect x="34" y="325" width="60" height="6" rx="2" fill="#E8E8F0"/>
              <rect x="120" y="325" width="80" height="6" rx="2" fill="#E8E8F0"/>
              <rect x="226" y="325" width="60" height="6" rx="2" fill="#E8E8F0"/>
            </svg>
          </div>
        </div>
      </div>

      {/* ─── TRUST BAR ─── */}
      <div className="sv__trust">
        <div className="sv__trust-inner">
          {STATS.map((s, i) => (
            <div key={i} className="sv__trust-item">
              <div className="sv__trust-val">{s.val}</div>
              <div className="sv__trust-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── WHAT WE DO ─── */}
      <div className="sv__features">
        <div className="sv__features-inner">
          <div className="sv__features-header">
            <span className="sv__tag">What You Get</span>
            <div className="sv__features-title">Everything You Need in One Dashboard</div>
            <p className="sv__features-sub">Six pillars of financial intelligence — personalised to your income, age, occupation, and goals.</p>
          </div>
          <div className="sv__features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="sv__feature">
                <div className="sv__feature-icon"><FeatureIcon type={f.icon} /></div>
                <div className="sv__feature-title">{f.title}</div>
                <p className="sv__feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── HOW IT WORKS ─── */}
      <div className="sv__how">
        <div className="sv__how-inner">
          <div className="sv__features-header">
            <span className="sv__tag">How It Works</span>
            <div className="sv__features-title">Three Steps to Financial Clarity</div>
          </div>
          <div className="sv__how-steps">
            {[
              { num: '01', title: 'Build Your Profile', desc: 'Answer 8 questions about your age, income, goals, and habits. Takes under 2 minutes.' },
              { num: '02', title: 'Get Your Roadmap', desc: 'Receive a personalised health score, risk profile, budget analysis, and action plan — instantly.' },
              { num: '03', title: 'Use the Tools', desc: 'SIP projections, EMI calculator, tax optimiser, debt payoff planner, investment comparison — all pre-filled from your profile.' },
            ].map((s, i) => (
              <div key={i} className="sv__how-step">
                <div className="sv__how-num">{s.num}</div>
                <div className="sv__how-title">{s.title}</div>
                <p className="sv__how-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CTA ─── */}
      <div className="sv__cta-section">
        <div className="sv__cta">
          <svg className="sv__cta-art" viewBox="0 0 700 140" preserveAspectRatio="xMidYMid slice">
            <circle cx="80" cy="60" r="50" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1"/>
            <circle cx="620" cy="50" r="40" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1"/>
            <path d="M 50 110 Q 200 30 380 70 T 660 30" fill="none" stroke="rgba(79,41,126,0.04)" strokeWidth="1" strokeDasharray="5 4"/>
          </svg>
          <div className="sv__cta-content">
            <div className="sv__cta-title">Your financial clarity starts here.</div>
            <p className="sv__cta-sub">Free. No credit card. No regulated advice. Just tools that make your money make sense.</p>
            <div className="sv__cta-actions">
              <a href="/finlit" className="sv__btn-primary sv__btn-primary--lg">Open Financial Dashboard <span>→</span></a>
              <a href="/individual" className="sv__btn-outline">For Individuals</a>
              <a href="/business" className="sv__btn-outline">For Businesses</a>
            </div>
          </div>
        </div>
      </div>
      <Footer/>

    </section>
    
  );
};

export default Save;